import { Fragment } from 'react'
import { Box, Stack } from '@mui/material'

import { SectionHeading } from '@/components/atoms/SectionHeading'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { type SoapContentItem } from '@/shared/types/yakurekiSoap'
import { RADIUS } from '@/shared/styles/radii'

import { CopyableHeading } from './CopyableHeading'
import { EditInputBase } from './EditInputBase'
import { EmptyLineAddSlot } from './EmptyLineAddSlot'
import { SoapLine } from './SoapLine'
import { SoapLineBulletSlot } from './SoapLineBulletSlot'
import { type LineEditing } from './hooks/useLineEditing'

// 採否の実装差を吸収する adapter。SOAP・訪問報告書とも採否の起点は is_selected フィールドで
// 統一 (index で識別・server 永続) だが、永続経路 (SOAP=updateSoap / 報告書=home-visit-report
// PATCH) が異なるため、SectionRenderer は per-line の struck 判定 (= 非採用) と toggle の 2 つ
// だけ見れば描画できるよう各 view が自分の経路で実装する。
// 採否は行単位のみ (見出しに一括除外は置かない) のため、section 単位の toggle は持たない。
export interface SectionStrikeAdapter {
  isStruck: (item: SoapContentItem, index: number) => boolean
  toggleLine: (item: SoapContentItem, index: number) => void
}

interface Props<K extends string> {
  sectionKey: K
  /** 見出しラベル。省略時は見出し行ごと出さない (タブ名が文書種別を担う単一文書タブ)。 */
  label?: string
  items: SoapContentItem[]
  editing: LineEditing<K>
  strike: SectionStrikeAdapter
  /** AI で改善して変わった行か (印を付けない view は省略)。 */
  isFlashing?: (index: number) => boolean
  /** 行の DOM id (変わった行へ scroll する view だけ渡す)。 */
  lineId?: (index: number) => string
  /** 見出しのコピー。label とセットで指定する (見出しなしでは使われない)。 */
  onCopy?: () => void
  copyTitle?: string
  medications: string[]
  onPlaceholderReplace: (
    placeholder: string,
    replacement: string,
    replaceAll?: boolean
  ) => Promise<boolean>
}

// SOAP 本体 (SoapBody) と 訪問報告書 (HomeVisitReportPanel) で共通の「1 セクション」の描画。
// 見出し(ラベル + コピー) / 行(採否・inline 編集・Enter 連続追加) / 空状態(EmptyLineAddSlot)
// を持つ。行編集の状態機械は editing(useLineEditing)、採否は strike adapter を注入する。
export function SectionRenderer<K extends string>({
  sectionKey,
  label,
  items,
  editing,
  strike,
  isFlashing,
  lineId,
  onCopy,
  copyTitle,
  medications,
  onPlaceholderReplace,
}: Props<K>) {
  // 未保存の新規行の枠なし入力行。afterIndex の直後 (afterIndex=-1 は先頭) に挿入。
  const renderPending = (afterIndex: number) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        borderRadius: `${RADIUS.sm}px`,
        bgcolor: colorTokens.overlay.selected,
      }}
    >
      <SoapLineBulletSlot />
      <EditInputBase
        value={editing.editingText}
        onChange={editing.setEditingText}
        // blur = 保存して抜ける。Enter で次の行へ移った直後に届く旧入力欄の blur は
        // useLineEditing 側が対象違いとして無効化するので、ここでの抑止は要らない。
        onBlur={() => {
          void editing.saveNewLine(sectionKey, afterIndex)
        }}
        onKeyDown={(e) => {
          // IME 変換中の Enter は確定操作なので行の確定に使わない (SoapLine の編集入力欄と対)。
          if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
            e.preventDefault()
            void editing.saveNewLine(sectionKey, afterIndex, {
              continueAfterSave: true,
            })
          }
          if (e.key === 'Escape') editing.cancel()
        }}
      />
    </Box>
  )

  return (
    <Box>
      {label != null &&
        (items.length > 0 && onCopy ? (
          <CopyableHeading
            label={label}
            title={copyTitle ?? ''}
            onCopy={onCopy}
            showCopyButton
          />
        ) : (
          // 行ゼロの section も見出しラベルは行あり section と同じ左レール (行頭) に置く.
          <SectionHeading>{label}</SectionHeading>
        ))}
      {items.length > 0 ? (
        // 行はガター (チェックボックス列) を先頭に持つので pl 不要. チェックボックスが左ガター
        // (x=0) に、 本文が左ガター幅ぶん字下げされて揃う (= 見出し左レールと同じ縦列).
        <Stack spacing={0.25} sx={{ mt: 0.25 }}>
          {items.map((item, i) => (
            <Fragment key={i}>
              <SoapLine
                id={lineId?.(i)}
                content={item.content}
                isStruck={strike.isStruck(item, i)}
                isFlashing={isFlashing?.(i) ?? false}
                isEditing={editing.isEditing(sectionKey, i)}
                editingText={editing.editingText}
                onEditingTextChange={editing.setEditingText}
                onToggleStrike={() => strike.toggleLine(item, i)}
                onStartEdit={() =>
                  editing.startEdit(sectionKey, i, item.content)
                }
                onEditBlur={() => {
                  void editing.saveEdit(sectionKey, i)
                }}
                onEditEnter={() => {
                  void editing.saveEdit(sectionKey, i, {
                    continueAfterSave: true,
                  })
                }}
                onEditEscape={editing.cancel}
                medications={medications}
                onPlaceholderReplace={onPlaceholderReplace}
              />
              {editing.hasNewLineAfter(sectionKey, i) && renderPending(i)}
            </Fragment>
          ))}
        </Stack>
      ) : editing.hasNewLineAfter(sectionKey, -1) ? (
        <Box sx={{ mt: 0.25 }}>{renderPending(-1)}</Box>
      ) : (
        <EmptyLineAddSlot onAdd={() => editing.openNewLine(sectionKey, -1)} />
      )}
    </Box>
  )
}
